import React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getQueryTypePagePath } from '../../../../utils';
import { QueryType } from '../../../../utils/apollo/graphqlSchema';
import * as BuilderInfoUI from '../../BuilderInfoUI';
import {
  ExpandEvent,
  InfoPopupHeader,
  MergeEvent,
  ShowInReport,
} from '../../components';
import CohortDetails from '../../components/CohortDetails';
import { useFullscreenContext } from '../../../../utils/contexts/FullscreenContext';
import { IReportBuilderInfoProps } from '../../types';
import { assertCondition } from '../../../../utils/types';
import useAnalysisBuilderInfo from '../../../../redux/selectorHooks/useAnalysisBuilderInfo';
import { theme } from '../../../../styles/theme';

const EventsContainer = styled(BuilderInfoUI.VerticalRow)`
  &:not(:first-child) {
    padding-top: 8px;
    border-top: 1px solid ${theme.colors.border};
  }
`;

export default ({
  analysisId,
  onShowInReport,
}: Pick<IReportBuilderInfoProps, 'analysisId' | 'onShowInReport'>) => {
  const location = useLocation();

  const { isFullScreen, toggleFullScreen } = useFullscreenContext();

  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  assertCondition(
    analysisBuilderInfo !== null &&
      analysisBuilderInfo?.__typename === 'CohortSample',
  );
  const {
    query: builderInfo,
    display,
    executedAt,
    description,
    creator,
  } = analysisBuilderInfo;

  const cohort = builderInfo.cohort;
  const sampleOperation = builderInfo.operation;
  const samplePercentage = builderInfo.samplePercentage;
  const sampleAbsolute = builderInfo.sampleAbsolute;
  const expandEvents = builderInfo.compare?.expandEvents;
  const mergeEvents = builderInfo.compare?.mergeEvents;

  const reportUrl = getQueryTypePagePath(QueryType.CohortSample, analysisId);

  const showInReportAvailable =
    `${location.pathname}${location.search}` !== reportUrl;

  const navigateToReport = () => {
    if (isFullScreen) {
      toggleFullScreen();
    }

    if (onShowInReport) {
      onShowInReport();
    }
  };

  return (
    <>
      <InfoPopupHeader
        name={display}
        description={description}
        creator={creator}
        createdAt={executedAt}
      />
      <BuilderInfoUI.MainContent>
        {cohort && (
          <CohortDetails
            cohort={cohort}
            sampleOperation={sampleOperation}
            samplePercentage={samplePercentage}
            sampleAbsolute={sampleAbsolute}
          />
        )}
        {((expandEvents && expandEvents?.length > 0) ||
          (mergeEvents && mergeEvents?.length > 0)) && (
          <BuilderInfoUI.VerticalRow>
            {expandEvents && expandEvents?.length > 0 && (
              <EventsContainer>
                {expandEvents.map((expandEvent, index) => (
                  <ExpandEvent
                    key={`expand-event-${index}`}
                    eventCondition={expandEvent.eventCondition}
                    field={expandEvent.field}
                    index={index}
                  />
                ))}
              </EventsContainer>
            )}
            {mergeEvents && mergeEvents?.length > 0 && (
              <EventsContainer>
                {mergeEvents.map((mergeEvent, index) => (
                  <MergeEvent
                    key={`merge-event-${index}`}
                    eventConditions={mergeEvent.eventConditions}
                    isNameAutoGenerated={mergeEvent.isNameAutoGenerated}
                    name={mergeEvent.name}
                    index={index}
                  />
                ))}
              </EventsContainer>
            )}
          </BuilderInfoUI.VerticalRow>
        )}
      </BuilderInfoUI.MainContent>
      {showInReportAvailable && (
        <BuilderInfoUI.FooterContainer>
          <ShowInReport
            reportUrl={reportUrl}
            onClick={navigateToReport}
            trackContext="cohorts"
          />
        </BuilderInfoUI.FooterContainer>
      )}
    </>
  );
};
